import React, { useState } from 'react';
import {
  SimpleForm,
  TextInput,
  Toolbar,
  SaveButton,
  useNotify,
  useRedirect,
  Title,
} from 'react-admin';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Chip,
  Tabs,
  Tab,
  Alert,
} from '@mui/material';
import UserReferenceArrayInputWithSearch from 'shared-fields/UserReferenceArrayInputWithSearch';
import PhoneNumberArrayInput from 'shared-fields/PhoneNumberArrayInput';
import ChannelFilterForm from 'shared-fields/ChannelFilterForm';
import { BASE_API_URL } from 'config';
import { authTokenKey } from 'authProvider';

// Function to calculate SMS pages
const calculateSmsPages = (message) => {
  if (!message) return 0;

  // Check if message contains non-GSM characters (Unicode)
  const gsmCharacters = /^[@£$¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&'()*+,\-./:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà^{}\\[~\\]|€]*$/;
  const isGsm = gsmCharacters.test(message);

  const maxLength = isGsm ? 160 : 70; // GSM 7-bit: 160 chars, Unicode: 70 chars
  const maxLengthMultiple = isGsm ? 153 : 67; // Subsequent SMS parts are shorter due to headers

  if (message.length <= maxLength) {
    return 1;
  }

  return Math.ceil((message.length - maxLength) / maxLengthMultiple) + 1;
};

// SMS Counter Component
function SmsCounter({ message }) {
  const pages = calculateSmsPages(message);
  const charCount = message ? message.length : 0;
  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 1, mt: -2,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {charCount}
        {' '}
        characters /
        {' '}
        {pages}
        {' '}
        page(s)
      </Typography>
    </Box>
  );
}

function CustomToolbar(props) {
  return (
    <Toolbar {...props} sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <Box />
      <SaveButton label="Send SMS" submitOnEnter />
    </Toolbar>
  );
}

function SendSms() {
  const notify = useNotify();
  const redirect = useRedirect();
  const [message, setMessage] = useState('');
  const [targetingMode, setTargetingMode] = useState(0); // 0: users, 1: phone numbers, 2: channel owners
  const [channelFilters, setChannelFilters] = useState({});

  const onSubmit = async (values) => {
    const { userIds, phoneNumbers, message } = values;

    // Determine the payload based on targeting mode
    let payload = { message };
    if (targetingMode === 0) {
      // Send to selected users
      if (!userIds || userIds.length === 0) {
        notify('Please select at least one user', { type: 'warning' });
        return;
      }
      payload = { ...payload, userIds };
    } else if (targetingMode === 1) {
      // Send to phone numbers
      if (!phoneNumbers || phoneNumbers.length === 0) {
        notify('Please add at least one phone number', { type: 'warning' });
        return;
      }
      payload = { ...payload, phoneNumbers };
    } else if (targetingMode === 2) {
      // Send to channel owners
      if (Object.keys(channelFilters).length === 0) {
        notify('Please apply at least one channel filter', { type: 'warning' });
        return;
      }
      payload = { ...payload, channelFilters };
    }
    try {
      const token = localStorage.getItem(authTokenKey);

      // Determine the API endpoint based on targeting mode
      const url = `${BASE_API_URL}send-sms`;
      // if (targetingMode === 0) {
      //   url = `${BASE_API_URL}/sms`;
      // } else if (targetingMode === 1) {
      //   url = `${BASE_API_URL}sms/phone-numbers`;
      // } else if (targetingMode === 2) {
      //   url = `${BASE_API_URL}sms/channel-owners`;
      // }
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result?.error || `HTTP error! status: ${response.status}`);
      }
      notify('SMS sent successfully', { type: 'success' });
      // redirect(-1); // go back to the previous page
    } catch (error) {
      notify(error.response?.data?.error || error.message || 'Error sending SMS', { type: 'warning' });
    }
  };

  const handleChannelFiltersChange = (filters) => {
    setChannelFilters(filters);
  };

  const getTargetingModeLabel = () => {
    switch (targetingMode) {
    case 0: return 'Select users and compose your message';
    case 1: return 'Add phone numbers and compose your message';
    case 2: return 'Filter channels by owners and compose your message';
    default: return 'Select targeting method and compose your message';
    }
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Title title="Send SMS" />
      <Card sx={{ maxWidth: 1000, margin: 'auto' }}>
        <CardHeader
          title={(
            <Typography variant="h5" component="h1">
              Send SMS Message
            </Typography>
          )}
          subheader={getTargetingModeLabel()}
        />
        <CardContent>
          {/* Targeting Mode Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs
              value={targetingMode}
              onChange={(e, newValue) => setTargetingMode(newValue)}
              aria-label="SMS targeting mode"
            >
              <Tab label="Send to Users" />
              <Tab label="Send to Phone Numbers" />
              <Tab label="Send to Channel Owners" />
            </Tabs>
          </Box>

          <SimpleForm
            onSubmit={onSubmit}
            toolbar={<CustomToolbar />}
          >
            {/* Targeting Inputs */}
            {targetingMode === 0 && (
              <UserReferenceArrayInputWithSearch
                source="userIds"
                label="Select Users (type to search)"
                fullWidth
              />
            )}

            {targetingMode === 1 && (
              <Box sx={{ mb: 2 }}>
                <PhoneNumberArrayInput
                  source="phoneNumbers"
                  label="Phone Numbers"
                  fullWidth
                />
                <Alert severity="info" sx={{ mt: 1 }}>
                  Enter phone numbers in the format: +989123456789 or 09123456789
                </Alert>
              </Box>
            )}

            {targetingMode === 2 && (
              <Box sx={{ mb: 2, width: '100%' }}>
                <ChannelFilterForm
                  label="Channel Filters (to target channel owners)"
                  fullWidth
                  onFiltersChange={handleChannelFiltersChange}
                />
                <Alert severity="info" sx={{ mt: 1 }}>
                  Apply filters to select channels. SMS will be sent to the owners of matching channels.
                </Alert>
              </Box>
            )}

            {/* Message Input */}
            <Box sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              mt: 2,
            }}
            >
              <TextInput
                source="message"
                label="Message"
                multiline
                fullWidth
                rows={4}
                required
                onChange={(e) => setMessage(e.target.value)}
                sx={{
                  textAlign: 'right',
                  direction: 'rtl',
                }}
              />
              <SmsCounter message={message} />
            </Box>
          </SimpleForm>
        </CardContent>
      </Card>
    </Box>
  );
}

export default SendSms;
